import React from 'react';
import { Modal } from '../../../../components/ui/Modal';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Textarea } from '../../../../components/ui/Textarea';
import { Checkbox } from '../../../../components/ui/Checkbox';

interface PlanFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingPlanId: string | null;
  name: string;
  setName: (name: string) => void;
  slug: string;
  setSlug: (slug: string) => void;
  description: string;
  setDescription: (desc: string) => void;
  maxMonthlySubmissions: number;
  setMaxMonthlySubmissions: (n: number) => void;
  maxTables: number;
  setMaxTables: (n: number) => void;
  maxTeamSeats: number;
  setMaxTeamSeats: (n: number) => void;
  retentionDays: number;
  setRetentionDays: (n: number) => void;
  maxStorageMB: number;
  setMaxStorageMB: (n: number) => void;
  maxFileSizeMB: number;
  setMaxFileSizeMB: (n: number) => void;
  priceMonthlyDollars: number;
  setPriceMonthlyDollars: (n: number) => void;
  priceYearlyDollars: number;
  setPriceYearlyDollars: (n: number) => void;
  isActive: boolean;
  setIsActive: (active: boolean) => void;
  isDefault: boolean;
  setIsDefault: (isDefault: boolean) => void;
  featuresText: string;
  setFeaturesText: (text: string) => void;
  isSaving: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

export const PlanFormModal: React.FC<PlanFormModalProps> = ({
  isOpen,
  onClose,
  editingPlanId,
  name,
  setName,
  slug,
  setSlug,
  description,
  setDescription,
  maxMonthlySubmissions,
  setMaxMonthlySubmissions,
  maxTables,
  setMaxTables,
  maxTeamSeats,
  setMaxTeamSeats,
  retentionDays,
  setRetentionDays,
  maxStorageMB,
  setMaxStorageMB,
  maxFileSizeMB,
  setMaxFileSizeMB,
  priceMonthlyDollars,
  setPriceMonthlyDollars,
  priceYearlyDollars,
  setPriceYearlyDollars,
  isActive,
  setIsActive,
  isDefault,
  setIsDefault,
  featuresText,
  setFeaturesText,
  isSaving,
  onSubmit,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingPlanId ? `Edit Plan: ${name}` : 'Create Subscription Plan'}
      maxWidth="2xl"
    >
      <form onSubmit={onSubmit} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Plan Name"
            type="text"
            size="xs"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Starter"
          />
          <Input
            label="Slug (kebab-case)"
            type="text"
            size="xs"
            required
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="e.g. starter"
            mono
          />
        </div>

        <Textarea
          label="Description"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief summary of tier purpose and audience"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Monthly Price ($ USD)"
            type="number"
            size="xs"
            min="0"
            value={priceMonthlyDollars}
            onChange={(e) => setPriceMonthlyDollars(Number(e.target.value))}
          />
          <Input
            label="Yearly Price ($ USD)"
            type="number"
            size="xs"
            min="0"
            value={priceYearlyDollars}
            onChange={(e) => setPriceYearlyDollars(Number(e.target.value))}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-cf-textSecondary mb-2">
            Plan Limits &amp; Quotas
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-cf-bg/40 p-4 rounded-lg border border-cf-border">
            <div>
              <Input
                label="Monthly Submissions"
                type="number"
                size="xs"
                min="0"
                value={maxMonthlySubmissions}
                onChange={(e) => setMaxMonthlySubmissions(Number(e.target.value))}
                helperText="Submissions per cycle"
              />
            </div>
            <div>
              <Input
                label="Max Tables"
                type="number"
                size="xs"
                min="0"
                value={maxTables}
                onChange={(e) => setMaxTables(Number(e.target.value))}
                helperText="Active tables allowed"
              />
            </div>
            <div>
              <Input
                label="Team Seats"
                type="number"
                size="xs"
                min="0"
                value={maxTeamSeats}
                onChange={(e) => setMaxTeamSeats(Number(e.target.value))}
                helperText="Seats + pending invites"
              />
            </div>

            <div>
              <Input
                label="Max Storage (MB)"
                type="number"
                size="xs"
                min="0"
                value={maxStorageMB}
                onChange={(e) => setMaxStorageMB(Number(e.target.value))}
                helperText="Total storage quota"
              />
            </div>
            <div>
              <Input
                label="Max File Size (MB)"
                type="number"
                size="xs"
                min="1"
                value={maxFileSizeMB}
                onChange={(e) => setMaxFileSizeMB(Number(e.target.value))}
                helperText="Max per upload file"
              />
            </div>
            <div>
              <Input
                label="Retention (Days)"
                type="number"
                size="xs"
                min="0"
                value={retentionDays}
                onChange={(e) => setRetentionDays(Number(e.target.value))}
                helperText="History retention"
              />
            </div>
          </div>
        </div>

        <Textarea
          label="Feature Bullets (one per line)"
          rows={4}
          value={featuresText}
          onChange={(e) => setFeaturesText(e.target.value)}
          placeholder="e.g. 50,000 monthly submissions&#10;50 datastore tables&#10;10 team seats"
        />

        <div className="flex flex-wrap items-center gap-6 pt-1">
          <Checkbox
            label="Plan is Active for Signups"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          <Checkbox
            label="Set as Default Plan for New Organizations"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
        </div>

        <div className="flex justify-end gap-2.5 pt-4 border-t border-cf-border">
          <Button variant="secondary" size="xs" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="xs"
            type="submit"
            isLoading={isSaving}
            disabled={isSaving}
          >
            {editingPlanId ? 'Save Changes' : 'Create Plan'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
